import type { ListBuilderConfiguration } from '../types';

/**
 * Top-level Contacts list configuration (Sales -> Customers -> Contacts).
 *
 * A cross-client, paginated `variant="main"` list of `client_contact` records —
 * the substrate for campaign call / follow-up lists. Columns: the contact
 * (name/title), actionable contact methods (email via ClickToEmail, phone via
 * ClickToDialPhone — both built-in `displayStyle`s, no page callback needed),
 * the parent company (name + address), and type.
 *
 * Data source: GET /client-contacts (ClientContactsListService), whose flat rows
 * carry the client_contact fields plus `clientName` / `clientAddress1` /
 * `clientCityStateZip` / `clientId`.
 */
export const CLIENT_CONTACT_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-contacts-all',
			cacheTTL: 300000,
			name: 'All Contacts',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'name-column',
					fields: [
						{
							id: 'contact-name',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: 'client_contact' },
								defaultValue: 'Unnamed Contact',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-name',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-title',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'title', objectTable: 'client_contact' },
								defaultValue: '',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-name', text: 'Name' },
						{ id: 'h-title', text: 'Title' },
					],
					width: 180,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'client_contact',
						},
					],
				},
				{
					id: 'contact-methods-column',
					fields: [
						{
							id: 'contact-email',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'email', objectTable: 'client_contact' },
							},
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'email',
						},
						{
							id: 'line-break-methods',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-phone',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'phone', objectTable: 'client_contact' },
							},
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'phone',
						},
					],
					headers: [{ id: 'h-methods', text: 'Email / Phone' }],
					width: 200,
					sortable: false,
				},
				{
					id: 'company-column',
					fields: [
						{
							id: 'contact-company',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientName',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-company',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'company-address1',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientAddress1',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-address',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'company-city-state-zip',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientCityStateZip',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-company', text: 'Company' }],
					width: 200,
					sortable: false,
				},
				{
					id: 'type-column',
					fields: [
						{
							id: 'contact-type-badge',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'typeLabel',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-type', text: 'Type' }],
					width: 100,
					align: 'center',
					sortable: false,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editContact',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'clients:contacts:update',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-name-column-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'contactSearch',
			'contactType',
			'contactClient',
			'campaigns',
		],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Contact',
			objectTable: 'client_contact',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'title', fieldLabel: 'Title' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'extension', fieldLabel: 'Extension' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'typeLabel', fieldLabel: 'Type' },
				{ fieldName: 'clientName', fieldLabel: 'Company' },
				{ fieldName: 'clientAddress1', fieldLabel: 'Company Address' },
				{
					fieldName: 'clientCityStateZip',
					fieldLabel: 'Company City/State/Zip',
				},
			],
		},
	],
	tableSorts: [
		{
			id: 'column-name-column-asc',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'name', objectTable: 'client_contact' },
			],
		},
	],
};
